import React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { canShowAdminAccess } from "../adminAccess";
import { logout } from "../logout";
import { useTheme } from "../context/ThemeContext";
import { ToastProvider } from "../context/ToastContext";
import WikiVersionBadge from "./WikiVersionBadge";

function navLinkClass({ isActive }) {
  return [
    "rounded-md px-3 py-2 text-sm font-medium transition-colors",
    isActive
      ? "bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100"
      : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800",
  ].join(" ");
}

export default function AppShell() {
  const { theme, toggleTheme } = useTheme();
  const { pathname } = useLocation();
  /** Document library is desktop backoffice; use full viewport width for wide tables. */
  const wideMain = pathname === "/rdwr_docs" || pathname === "/rfp-feeder";
  const showAccess = canShowAdminAccess();

  return (
    <ToastProvider>
      <div className="flex min-h-screen flex-col bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur dark:border-slate-700/80 dark:bg-slate-900/90">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <NavLink
              to="/"
              className="truncate text-lg font-semibold tracking-tight text-slate-900 dark:text-white"
            >
              Radware Wiki
            </NavLink>
            <WikiVersionBadge />
          </div>

          <nav
            className="flex flex-1 flex-wrap items-center gap-1 sm:justify-center"
            aria-label="Primary"
          >
            <NavLink to="/" end className={navLinkClass}>
              Home
            </NavLink>
            <NavLink to="/qa" className={navLinkClass}>
              Q&amp;A
            </NavLink>
            <NavLink to="/editor" className={navLinkClass}>
              Editor
            </NavLink>
            <NavLink to="/rfp-feeder" className={navLinkClass}>
              RFP
            </NavLink>
            <NavLink to="/activity" className={navLinkClass}>
              Activity
            </NavLink>
            {showAccess && (
              <NavLink to="/access" className={navLinkClass}>
                Access
              </NavLink>
            )}
          </nav>

          <div className="flex shrink-0 items-center justify-end gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="min-h-[44px] min-w-[44px] rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800 sm:min-h-0 sm:min-w-0"
              aria-label={
                theme === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
            >
              {theme === "dark" ? "Light" : "Dark"}
            </button>
            <button
              type="button"
              onClick={logout}
              className="min-h-[44px] rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800 sm:min-h-0"
            >
              Log out
            </button>
          </div>
        </div>
      </header>

      <main
        className={`mx-auto w-full min-w-0 flex-1 px-4 py-6 ${wideMain ? "max-w-none" : "max-w-7xl"}`}
      >
        <Outlet />
      </main>
    </div>
    </ToastProvider>
  );
}
